/**
 * PostHog Types and Configuration
 *
 * This file contains only types and configuration helpers for PostHog.
 * For PostHog functionality, use the usePostHog hook instead of a service.
 */

// PostHog Configuration Types
export interface PostHogConfig {
  apiKey: string;
  host: string;
  environment: string;
  enableSessionRecording: boolean;
  enableToolbar: boolean;
  isDisabled: boolean; // New flag to completely disable PostHog for local/dev environments
}

export interface PostHogEventProperties {
  [key: string]: string | number | boolean | null | undefined | string[] | number[];
}

export type PostHogGroupType = 'tenant' | 'domain' | 'subject' | 'survey_instance';

// Helper function to create PostHog configuration with environment-specific settings
export const createPostHogConfig = (): PostHogConfig => {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || '';
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';
  const environment = import.meta.env.VITE_POSTHOG_ENVIRONMENT || 'dev';

  // Environment-specific configuration
  const environmentConfig = getEnvironmentConfig(environment);

  // Determine if PostHog should be completely disabled for this environment
  const isDisabled = shouldDisablePostHog(environment);

  return {
    apiKey,
    host,
    environment,
    enableSessionRecording: environmentConfig.enableSessionRecording,
    enableToolbar: environmentConfig.enableToolbar,
    isDisabled,
  };
};

// Environment-specific PostHog configurations
const getEnvironmentConfig = (environment: string) => {
  switch (environment) {
    case 'prod':
      return {
        enableSessionRecording: true, // Enable for error-based recording
        enableToolbar: false, // Never enable in production
        debugMode: false,
        sessionRecordingSampleRate: 1, // Low sample rate (5%) - errors will trigger full recording
      };

    case 'staging':
      return {
        enableSessionRecording: true, // Enable for error-based recording only
        enableToolbar: false, // Keep disabled for clean testing
        debugMode: false,
        sessionRecordingSampleRate: 0, // No automatic recording - only on errors
      };

    case 'dev':
    case 'local':
    default:
      return {
        enableSessionRecording: true, // Enable for error-based recording only
        enableToolbar: import.meta.env.VITE_POSTHOG_ENABLE_TOOLBAR === 'true',
        debugMode: true,
        sessionRecordingSampleRate: 0, // No automatic recording - only on errors
      };
  }
};

/**
 * Determine if PostHog should be completely disabled based on environment
 * PostHog is disabled for local and dev environments to reduce
 * API call pollution and unnecessary feedback during development
 */
const shouldDisablePostHog = (environment: string): boolean => {
  // Disable PostHog for local and dev environments
  // Keep enabled for staging, prod, and demo
  const disabledEnvironments = ['dev', 'local', 'development'];
  return disabledEnvironments.includes(environment.toLowerCase());
};
